import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail, getToken, API } from "../lib/api";

const SENIORITY = ["junior", "confirmé", "senior", "expert"];
const REMOTE = ["onsite", "hybrid", "remote"];
const LANGS = ["fr", "en", "es", "de", "it", "pt", "nl"];
const EMPTY = {
  display_name: "",
  real_name: "",
  title: "",
  skills: [],
  seniority: "confirmé",
  tjm_target: 500,
  availability_date: new Date().toISOString().slice(0, 10),
  remote_policy: "hybrid",
  languages: ["fr", "en"],
  years_experience: 5,
  bio: "",
  redact_identity: false,
  location_city: "",
  catalog_opt_in: false,
};

export default function RosterForm({ mode }) {
  const { t } = useTranslation();
  const { organization } = useAuth();
  const hasActiveSub = organization?.subscription_status === "active" || organization?.subscription_status === "trialing";
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = mode === "edit";

  const [form, setForm] = useState(EMPTY);
  const [skillInput, setSkillInput] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cvUrl, setCvUrl] = useState(null);
  const [cvFilename, setCvFilename] = useState(null);
  const [savedId, setSavedId] = useState(id || null);
  const fileInputRef = useRef(null);

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e,
    }));

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/consultants/${id}`);
        setForm({
          display_name: data.display_name || "",
          real_name: data.real_name || "",
          title: data.title || "",
          skills: data.skills || [],
          seniority: data.seniority,
          tjm_target: data.tjm_target,
          availability_date: data.availability_date,
          remote_policy: data.remote_policy,
          languages: data.languages || [],
          years_experience: data.years_experience,
          bio: data.bio || "",
          redact_identity: !!data.redact_identity,
          location_city: data.location_city || "",
          catalog_opt_in: !!data.catalog_opt_in,
        });
        setCvUrl(data.cv_url || null);
        setSavedId(data.id);
      } catch (err) {
        toast.error(
          formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic")
        );
        navigate("/roster");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate, t]);

  const addSkill = (raw) => {
    const value = (raw || "").replace(/^[,\s]+|[,\s]+$/g, "").trim();
    if (!value) return;
    setForm((f) => (f.skills.includes(value) ? f : { ...f, skills: [...f.skills, value] }));
    setSkillInput("");
  };
  const onSkillKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addSkill(skillInput);
    } else if (e.key === "Backspace" && !skillInput && form.skills.length) {
      setForm((f) => ({ ...f, skills: f.skills.slice(0, -1) }));
    }
  };
  const removeSkill = (s) =>
    setForm((f) => ({ ...f, skills: f.skills.filter((x) => x !== s) }));

  const toggleLang = (l) =>
    setForm((f) => ({
      ...f,
      languages: f.languages.includes(l)
        ? f.languages.filter((x) => x !== l)
        : [...f.languages, l],
    }));

  const pseudoPreview = useMemo(() => {
    if (!savedId) return t("roster.redact_pseudo_placeholder");
    return `Consultant #${savedId.slice(-6).toUpperCase()}`;
  }, [savedId, t]);

  const submit = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        tjm_target: Number(form.tjm_target),
        years_experience: Number(form.years_experience),
        real_name: form.real_name || null,
        bio: form.bio || null,
      };
      let newId = savedId;
      if (savedId) {
        await api.patch(`/consultants/${savedId}`, payload);
        toast.success(t("roster.saved_toast"));
      } else {
        const { data } = await api.post("/consultants", payload);
        newId = data.id;
        setSavedId(newId);
        toast.success(t("roster.created_toast"));
      }
      return newId;
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic")
      );
      return null;
    } finally {
      setSaving(false);
    }
  }, [form, isEdit, savedId, t]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const newId = await submit();
    if (newId) navigate("/roster");
  };

  const onUploadCv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error(t("roster.cv_error_type"));
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("roster.cv_error_size"));
      e.target.value = "";
      return;
    }
    // If we haven't saved yet, save first so the backend has a consultant id.
    let targetId = savedId;
    if (!targetId) {
      targetId = await submit();
      if (!targetId) {
        e.target.value = "";
        return;
      }
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/consultants/${targetId}/cv`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCvUrl(data.cv_url);
      setCvFilename(file.name);
      toast.success(t("roster.cv_uploaded_toast"));
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic")
      );
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const openCv = async () => {
    if (!savedId) return;
    // Use fetch to send auth header, then blob-URL to open the PDF in a new tab.
    try {
      const res = await fetch(`${API}/consultants/${savedId}/cv`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (_) {
      toast.error(t("common.error_generic"));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="p-10 text-center text-slate-500">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="roster-form-page">
      <Toaster richColors position="top-right" />
      <Header />
      <main className="mx-auto max-w-3xl px-6 py-10">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">
              {isEdit ? t("roster.edit_eyebrow") : t("roster.create_eyebrow")}
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              {isEdit ? t("roster.edit_title") : t("roster.create_title")}
            </h1>
          </div>
          <Link
            to="/roster"
            className="text-sm font-medium text-slate-600 hover:text-slate-900"
            data-testid="roster-form-back"
          >
            ← {t("common.back")}
          </Link>
        </div>

        <form onSubmit={onSubmit} className="space-y-6" data-testid="roster-form">
          {/* Identity block */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-base font-semibold text-slate-900">
              {t("roster.section_identity")}
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <TextField label={t("roster.display_name")} value={form.display_name} onChange={set("display_name")} required testId="rf-display-name" />
              <TextField label={t("roster.real_name")} value={form.real_name} onChange={set("real_name")} hint={t("roster.real_name_hint")} testId="rf-real-name" />
              <div className="md:col-span-2 flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <input
                  id="redact"
                  type="checkbox"
                  checked={form.redact_identity}
                  onChange={set("redact_identity")}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  data-testid="rf-redact-toggle"
                />
                <label htmlFor="redact" className="flex-1 text-sm text-slate-700">
                  <span className="font-medium text-slate-900">{t("roster.redact_label")}</span>
                  <br />
                  <span className="text-slate-600">
                    {t("roster.redact_help")}{" "}
                    <span
                      className="mt-1 inline-block rounded-md bg-white px-2 py-0.5 font-mono text-xs text-slate-700 ring-1 ring-slate-200"
                      data-testid="rf-redact-preview"
                    >
                      {form.redact_identity ? pseudoPreview : form.display_name || t("roster.redact_pseudo_placeholder")}
                    </span>
                  </span>
                </label>
              </div>
            </div>
          </section>

          {/* Profile block */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-base font-semibold text-slate-900">
              {t("roster.section_profile")}
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <TextField label={t("roster.field_title")} value={form.title} onChange={set("title")} required testId="rf-title" className="md:col-span-2" />
              <SelectField label={t("roster.seniority")} value={form.seniority} onChange={set("seniority")} testId="rf-seniority">
                {SENIORITY.map((s) => (
                  <option key={s} value={s}>{t(`roster.seniority_${s}`)}</option>
                ))}
              </SelectField>
              <TextField label={t("roster.years_experience")} type="number" min={0} max={60} value={form.years_experience} onChange={set("years_experience")} testId="rf-years" />
              <TextField label={t("roster.tjm")} type="number" min={0} max={10000} value={form.tjm_target} onChange={set("tjm_target")} suffix="/day" testId="rf-tjm" />
              <TextField label={t("roster.availability")} type="date" value={form.availability_date} onChange={set("availability_date")} testId="rf-availability" />
              <TextField label={t("mission.city")} value={form.location_city} onChange={set("location_city")} testId="rf-location-city" />
              <SelectField label={t("roster.remote")} value={form.remote_policy} onChange={set("remote_policy")} testId="rf-remote">
                {REMOTE.map((r) => (
                  <option key={r} value={r}>{t(`roster.remote_${r}`)}</option>
                ))}
              </SelectField>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  {t("roster.languages")}
                </label>
                <div className="flex flex-wrap gap-1.5" data-testid="rf-languages">
                  {LANGS.map((l) => {
                    const active = form.languages.includes(l);
                    return (
                      <button
                        type="button"
                        key={l}
                        onClick={() => toggleLang(l)}
                        data-testid={`rf-lang-${l}`}
                        className={
                          "rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider transition-colors " +
                          (active
                            ? "bg-indigo-600 text-white shadow-sm"
                            : "bg-white text-slate-700 ring-1 ring-slate-300 hover:bg-slate-100")
                        }
                      >
                        {l}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  {t("roster.skills")}
                </label>
                <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 py-1.5 shadow-sm focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20">
                  {form.skills.map((s) => (
                    <span key={s} className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700" data-testid={`rf-skill-${s}`}>
                      {s}
                      <button type="button" onClick={() => removeSkill(s)} className="text-indigo-500 hover:text-indigo-700">×</button>
                    </span>
                  ))}
                  <input
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={onSkillKey}
                    onBlur={() => addSkill(skillInput)}
                    placeholder={t("roster.skills_placeholder")}
                    className="min-w-[10ch] flex-1 border-0 bg-transparent px-1 py-0.5 text-sm text-slate-900 focus:outline-none focus:ring-0"
                    data-testid="rf-skill-input"
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500">{t("roster.skills_hint")}</p>
              </div>
              <div className="md:col-span-2">
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  {t("roster.bio")}
                </label>
                <textarea
                  value={form.bio}
                  onChange={set("bio")}
                  rows={4}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  data-testid="rf-bio"
                />
              </div>
            </div>
          </section>

          {/* Catalog opt-in block */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="rf-catalog-section">
            <h2 className="text-base font-semibold text-slate-900">{t("cvtheque.roster_section_title")}</h2>
            <div className="mt-4 flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <input
                id="catalog"
                type="checkbox"
                checked={form.catalog_opt_in}
                onChange={set("catalog_opt_in")}
                disabled={!hasActiveSub}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                data-testid="rf-catalog-toggle"
              />
              <label htmlFor="catalog" className="flex-1 text-sm text-slate-700">
                <span className="font-medium text-slate-900">{t("cvtheque.roster_opt_in_label")}</span>
                <br />
                <span className="text-slate-600">{t("cvtheque.roster_opt_in_help")}</span>
              </label>
            </div>
            {!hasActiveSub && (
              <div className="mt-3 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="rf-catalog-paywall-note">
                <span>🔒 {t("cvtheque.roster_disabled_tooltip")}</span>
                <Link to="/settings/subscription" className="font-semibold text-amber-900 underline">
                  {t("cvtheque.roster_go_subscribe")}
                </Link>
              </div>
            )}
          </section>

          {/* CV block */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-base font-semibold text-slate-900">{t("roster.section_cv")}</h2>
            <p className="mt-1 text-sm text-slate-600">{t("roster.cv_help")}</p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                onChange={onUploadCv}
                className="hidden"
                data-testid="rf-cv-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                data-testid="rf-cv-upload-btn"
              >
                {uploading
                  ? t("common.loading")
                  : cvUrl
                    ? t("roster.cv_replace")
                    : t("roster.cv_upload")}
              </button>
              {cvUrl && (
                <>
                  <span className="text-sm text-emerald-700" data-testid="rf-cv-status">
                    ✓ {cvFilename || t("roster.cv_present")}
                  </span>
                  <button
                    type="button"
                    onClick={openCv}
                    className="text-sm font-medium text-indigo-700 hover:text-indigo-800"
                    data-testid="rf-cv-view"
                  >
                    {t("roster.cv_view")}
                  </button>
                </>
              )}
            </div>
          </section>

          <div className="flex justify-end gap-3">
            <Link
              to="/roster"
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="rf-cancel-btn"
            >
              {t("common.cancel")}
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-60"
              data-testid="rf-submit-btn"
            >
              {saving ? t("common.loading") : isEdit ? t("common.save") : t("roster.create_submit")}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", required, testId, className = "", hint, suffix, min, max }) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      <div className="relative">
        <input
          type={type}
          value={value ?? ""}
          onChange={onChange}
          required={required}
          min={min}
          max={max}
          data-testid={testId}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
        />
        {suffix && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-slate-500">
            {suffix}
          </span>
        )}
      </div>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

function SelectField({ label, value, onChange, children, testId }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      <select
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
      >
        {children}
      </select>
    </div>
  );
}
